import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { LogoMark } from "@/components/Logo";
import { AREAS, areaBand, globalBand } from "@/questions";
import { downloadCampaignPdf } from "@/reportDownload";
import { Copy, LogOut, Plus, BarChart3, Download, Presentation } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full border border-navy-900/15 bg-white px-4 py-3 text-sm outline-none transition-colors focus:border-gold";

const usePrivateMeta = (title) => {
  useEffect(() => {
    document.title = title;
    let m = document.querySelector('meta[name="robots"]');
    if (!m) {
      m = document.createElement("meta");
      m.name = "robots";
      document.head.appendChild(m);
    }
    m.content = "noindex, nofollow";
  }, [title]);
};

const Login = ({ onLogin }) => {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem("eclenia-admin-token", data.token);
      onLogin(data.token);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Accesso non riuscito");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-cream px-5 grain relative" data-testid="admin-login">
      <form onSubmit={submit} className="w-full max-w-sm border border-navy-900/10 bg-white p-10" data-testid="admin-login-form">
        <LogoMark theme="light" size={36} />
        <h1 className="mt-6 font-serif text-2xl tracking-tight text-navy-900">Area riservata</h1>
        <p className="mt-2 text-sm text-navy-900/55">ECLENIA — Questionario di Benessere Aziendale</p>
        <input
          type="password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Password amministratore"
          className={`${inputCls} mt-7`}
          data-testid="admin-password-input"
        />
        {error && <p className="mt-3 text-sm text-red-700" data-testid="admin-login-error">{error}</p>}
        <button
          type="submit"
          disabled={loading}
          className="mt-5 w-full bg-navy-900 py-3.5 text-sm font-medium tracking-wide text-cream transition-colors hover:bg-sage-700 disabled:opacity-60"
          data-testid="admin-login-submit"
        >
          {loading ? "Accesso in corso…" : "Accedi"}
        </button>
      </form>
    </div>
  );
};

const NewCampaignForm = ({ token, onCreated }) => {
  const [name, setName] = useState("");
  const [departments, setDepartments] = useState("");
  const [target, setTarget] = useState("");
  const [createdLink, setCreatedLink] = useState(null);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const deps = departments.split(",").map((d) => d.trim()).filter(Boolean);
      const { data } = await axios.post(
        `${API}/admin/campaigns`,
        { name, departments: deps, target: parseInt(target, 10) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const link = `${window.location.origin}/q/${data.token}`;
      setCreatedLink(link);
      setName("");
      setDepartments("");
      setTarget("");
      toast.success("Campagna creata");
      onCreated(data);
    } catch {
      toast.error("Errore nella creazione della campagna");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="border border-navy-900/10 bg-white p-6" data-testid="new-campaign-form">
      <p className="flex items-center gap-2 text-xs font-medium uppercase tracking-[0.22em] text-gold-deep">
        <Plus size={14} /> Nuova campagna
      </p>
      <input required value={name} onChange={(e) => setName(e.target.value)} placeholder="Nome azienda *" className={`${inputCls} mt-4`} data-testid="campaign-name-input" />
      <input value={departments} onChange={(e) => setDepartments(e.target.value)} placeholder="Reparti (opzionale, separati da virgola)" className={`${inputCls} mt-3`} data-testid="campaign-departments-input" />
      <input required type="number" min="1" value={target} onChange={(e) => setTarget(e.target.value)} placeholder="Questionari attesi (target) *" className={`${inputCls} mt-3`} data-testid="campaign-target-input" />
      <button type="submit" disabled={loading} className="mt-4 w-full bg-navy-900 py-3 text-sm font-medium text-cream transition-colors hover:bg-sage-700 disabled:opacity-60" data-testid="campaign-create-submit">
        {loading ? "Creazione…" : "Crea e genera link"}
      </button>
      {createdLink && (
        <div className="mt-4 border border-gold/40 bg-sand p-4" data-testid="campaign-link-box">
          <p className="text-[11px] uppercase tracking-[0.18em] text-navy-900/50">Link unico da distribuire</p>
          <p className="mt-1.5 break-all font-mono text-xs text-navy-900">{createdLink}</p>
          <button
            type="button"
            onClick={() => { navigator.clipboard.writeText(createdLink); toast.success("Link copiato"); }}
            className="mt-3 flex items-center gap-2 border border-navy-900/20 px-4 py-2 text-xs font-medium text-navy-900 transition-colors hover:bg-navy-900 hover:text-cream"
            data-testid="campaign-copy-link-btn"
          >
            <Copy size={13} /> Copia link
          </button>
        </div>
      )}
    </form>
  );
};

const StatsView = ({ stats, onFilterDept, activeDept, token }) => {
  const c = stats.campaign;
  const source = activeDept ? stats.departments?.find((d) => d.name === activeDept) : stats;
  const pct = stats.target > 0 ? Math.min(100, Math.round((stats.completed / stats.target) * 100)) : 0;
  const [pdfLoading, setPdfLoading] = useState(false);

  const downloadPdf = async () => {
    setPdfLoading(true);
    try {
      await downloadCampaignPdf(token, c.id, "it", c.name);
      toast.success("Report PDF scaricato");
    } catch {
      toast.error("Errore nella generazione del report");
    } finally {
      setPdfLoading(false);
    }
  };

  return (
    <div data-testid="stats-view">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="font-serif text-2xl tracking-tight text-navy-900 sm:text-3xl" data-testid="stats-campaign-name">{c.name}</h2>
          <p className="mt-1 font-mono text-xs text-navy-900/45">/q/{c.token}</p>
        </div>
        {stats.departments && (
          <select
            value={activeDept || ""}
            onChange={(e) => onFilterDept(e.target.value || null)}
            className="border border-navy-900/15 bg-white px-4 py-2.5 text-sm outline-none"
            data-testid="department-filter"
          >
            <option value="">Tutti i reparti</option>
            {stats.departments.map((d) => (
              <option key={d.name} value={d.name}>{d.name} ({d.completed})</option>
            ))}
          </select>
        )}
      </div>

      <div className="mt-5 flex flex-wrap gap-3">
        <button
          type="button"
          onClick={downloadPdf}
          disabled={pdfLoading}
          className="flex items-center gap-2 bg-navy-900 px-5 py-2.5 text-xs font-medium uppercase tracking-[0.14em] text-cream transition-colors hover:bg-sage-700 disabled:opacity-60"
          data-testid="report-pdf-btn"
        >
          <Download size={14} /> {pdfLoading ? "Generazione…" : "Scarica Report PDF"}
        </button>
        <Link
          to={`/admin/presentazione/${c.id}`}
          className="flex items-center gap-2 border border-navy-900/25 px-5 py-2.5 text-xs font-medium uppercase tracking-[0.14em] text-navy-900 transition-colors hover:bg-navy-900 hover:text-cream"
          data-testid="presentation-mode-btn"
        >
          <Presentation size={14} /> Modalità Presentazione
        </Link>
      </div>

      <div className="mt-8 grid gap-px border border-navy-900/10 bg-navy-900/10 sm:grid-cols-3">
        <div className="bg-cream p-6" data-testid="stat-completion">
          <p className="font-mono text-3xl text-navy-900">{stats.completed}<span className="text-lg text-navy-900/40">/{stats.target}</span></p>
          <p className="mt-2 text-sm font-medium text-navy-900/70">Questionari completati</p>
          <div className="mt-3 h-1.5 w-full bg-navy-900/10">
            <div className="h-1.5 bg-sage-700 transition-all duration-700" style={{ width: `${pct}%` }} />
          </div>
          <p className="mt-2 text-xs text-navy-900/45">{pct}% di completamento</p>
        </div>
        <div className="bg-cream p-6" data-testid="stat-global">
          <p className="font-mono text-3xl text-navy-900">{source.avg_global}<span className="text-lg text-navy-900/40">/325</span></p>
          <p className="mt-2 text-sm font-medium text-navy-900/70">Punteggio globale medio{activeDept ? ` — ${activeDept}` : ""}</p>
          <p className="mt-2 inline-block border border-sage-200 bg-sage-50 px-3 py-1 text-[11px] uppercase tracking-[0.15em] text-sage-700" data-testid="stat-global-band">
            {source.completed > 0 ? globalBand(Math.round(source.avg_global)) : "Nessun dato"}
          </p>
        </div>
        <div className="bg-cream p-6" data-testid="stat-priority">
          <p className="font-mono text-3xl text-navy-900">{source.area_avgs.filter((a) => a < 15 && a > 0).length}</p>
          <p className="mt-2 text-sm font-medium text-navy-900/70">Aree prioritarie</p>
          <p className="mt-2 text-xs text-navy-900/45">Aree con media inferiore a 15/25 evidenziate sotto</p>
        </div>
      </div>

      <div className="mt-10">
        <p className="flex items-center gap-2 text-xs font-medium uppercase tracking-[0.22em] text-gold-deep">
          <BarChart3 size={14} /> Punteggio medio per area (0–25)
        </p>
        <div className="mt-5 space-y-4" data-testid="area-chart">
          {AREAS.map((area, i) => {
            const avg = source.area_avgs[i] || 0;
            const priority = avg > 0 && avg < 15;
            return (
              <div key={area.name} data-testid={`area-row-${i}`}>
                <div className="flex items-baseline justify-between gap-4">
                  <p className="text-sm font-medium text-navy-900">
                    {area.name}
                    {priority && (
                      <span className="ml-3 border border-gold-deep/50 bg-gold-light/40 px-2 py-0.5 text-[10px] uppercase tracking-[0.12em] text-gold-deep" data-testid={`area-priority-${i}`}>
                        Prioritaria
                      </span>
                    )}
                  </p>
                  <p className="font-mono text-sm text-navy-900/60">
                    {source.completed > 0 ? avg.toFixed(1) : "—"} <span className="text-xs text-navy-900/35">· {source.completed > 0 ? areaBand(Math.round(avg)) : ""}</span>
                  </p>
                </div>
                <div className="mt-1.5 h-2.5 w-full bg-navy-900/8">
                  <div
                    className={`h-2.5 transition-all duration-700 ${priority ? "bg-gold-deep" : "bg-sage-700"}`}
                    style={{ width: `${(avg / 25) * 100}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const Dashboard = ({ token, onLogout }) => {
  const [campaigns, setCampaigns] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [stats, setStats] = useState(null);
  const [activeDept, setActiveDept] = useState(null);
  const headers = { Authorization: `Bearer ${token}` };

  const loadCampaigns = async () => {
    const { data } = await axios.get(`${API}/admin/campaigns`, { headers });
    setCampaigns(data);
    if (data.length && !selectedId) setSelectedId(data[0].id);
  };

  useEffect(() => {
    loadCampaigns().catch((e) => {
      if (e.response?.status === 401) onLogout();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedId) return;
    setActiveDept(null);
    setStats(null);
    axios
      .get(`${API}/admin/campaigns/${selectedId}/stats`, { headers })
      .then(({ data }) => setStats(data))
      .catch((e) => { if (e.response?.status === 401) onLogout(); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  return (
    <div className="min-h-screen bg-cream" data-testid="admin-dashboard">
      <header className="border-b border-navy-900/10 bg-cream/90 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 sm:px-8">
          <div className="flex items-center gap-3">
            <LogoMark theme="light" size={28} />
            <div>
              <p className="font-serif text-lg tracking-tight text-navy-900">ECLENIA</p>
              <p className="text-[9px] uppercase tracking-[0.28em] text-navy-900/50">Area riservata · Benessere Aziendale</p>
            </div>
          </div>
          <button onClick={onLogout} className="flex items-center gap-2 text-sm text-navy-900/60 transition-colors hover:text-navy-900" data-testid="admin-logout-btn">
            <LogOut size={15} /> Esci
          </button>
        </div>
      </header>

      <div className="mx-auto grid max-w-7xl gap-8 px-5 py-10 sm:px-8 lg:grid-cols-12">
        <aside className="space-y-6 lg:col-span-4">
          <div className="border border-navy-900/10 bg-white p-6" data-testid="campaign-list">
            <p className="text-xs font-medium uppercase tracking-[0.22em] text-gold-deep">Campagne</p>
            {campaigns.length === 0 && <p className="mt-4 text-sm text-navy-900/50">Nessuna campagna ancora.</p>}
            <div className="mt-4 space-y-2">
              {campaigns.map((c) => (
                <button
                  key={c.id}
                  onClick={() => setSelectedId(c.id)}
                  data-testid={`campaign-select-${c.id}`}
                  className={`w-full border px-4 py-3 text-left transition-colors ${
                    selectedId === c.id ? "border-navy-900 bg-navy-900 text-cream" : "border-navy-900/15 bg-cream text-navy-900 hover:border-navy-900/40"
                  }`}
                >
                  <span className="block text-sm font-medium">{c.name}</span>
                  <span className={`mt-0.5 block text-xs ${selectedId === c.id ? "text-cream/60" : "text-navy-900/45"}`}>
                    {c.completed}/{c.target} completati
                  </span>
                </button>
              ))}
            </div>
          </div>
          <NewCampaignForm token={token} onCreated={(c) => { setSelectedId(c.id); loadCampaigns(); }} />
        </aside>

        <main className="lg:col-span-8">
          {stats ? (
            <StatsView stats={stats} onFilterDept={setActiveDept} activeDept={activeDept} token={token} />
          ) : selectedId ? (
            <div className="flex h-full items-center justify-center border border-dashed border-navy-900/15 p-16 text-sm text-navy-900/45" data-testid="stats-loading">
              Caricamento dati campagna…
            </div>
          ) : (
            <div className="flex h-full items-center justify-center border border-dashed border-navy-900/15 p-16 text-sm text-navy-900/45" data-testid="stats-empty">
              Crea la prima campagna per generare un link questionario.
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default function Admin() {
  usePrivateMeta("Area riservata — ECLENIA");
  const [token, setToken] = useState(() => localStorage.getItem("eclenia-admin-token"));
  const logout = () => {
    localStorage.removeItem("eclenia-admin-token");
    setToken(null);
  };
  return token ? <Dashboard token={token} onLogout={logout} /> : <Login onLogin={setToken} />;
}
